/**
 * Serviço de Coleta de Informações do Dispositivo
 * Responsável por coletar dados técnicos e características do dispositivo
 */

export class DeviceService {
    constructor(config, logger) {
        this.config = config;
        this.logger = logger.child('DeviceService');
        this.cache = new Map();
        this.cacheTimeout = 300000; // 5 minutos
        this.stats = {
            collectionsPerformed: 0,
            cacheHits: 0,
            cacheMisses: 0,
            errors: 0
        };
    }
    
    /**
     * Coleta informações completas do dispositivo
     */
    async collectDeviceInfo() {
        const cacheKey = 'device_info';
        
        // Verifica cache
        if (this.cache.has(cacheKey)) {
            const cached = this.cache.get(cacheKey);
            if (Date.now() - cached.timestamp < this.cacheTimeout) {
                this.stats.cacheHits++;
                this.logger.debug('Informações do dispositivo obtidas do cache');
                return cached.data;
            }
        }
        
        this.stats.cacheMisses++;
        this.stats.collectionsPerformed++;
        
        try {
            const deviceInfo = {
                // Informações básicas
                basic: await this.getBasicInfo(),
                
                // Informações do navegador
                browser: await this.getBrowserInfo(),
                
                // Informações da tela
                screen: await this.getScreenInfo(),
                
                // Informações de hardware
                hardware: await this.getHardwareInfo(),
                
                // Informações de rede
                network: await this.getNetworkInfo(),
                
                // Informações de localização
                location: await this.getLocationInfo(),
                
                // Informações de mídia
                media: await this.getMediaInfo(),
                
                // Informações de sensores
                sensors: await this.getSensorInfo(),
                
                // Informações de segurança
                security: await this.getSecurityInfo(),
                
                // Informações de performance
                performance: await this.getPerformanceInfo(),
                
                // Timestamp da coleta
                timestamp: Date.now()
            };
            
            // Armazena no cache
            this.cache.set(cacheKey, {
                data: deviceInfo,
                timestamp: Date.now()
            });
            
            this.logger.info('Informações do dispositivo coletadas com sucesso');
            return deviceInfo;
            
        } catch (error) {
            this.stats.errors++;
            this.logger.error('Erro ao coletar informações do dispositivo', { error: error.message });
            throw error;
        }
    }
    
    /**
     * Obtém informações básicas
     */
    async getBasicInfo() {
        return {
            userAgent: navigator.userAgent,
            language: navigator.language,
            languages: navigator.languages || [],
            platform: navigator.platform,
            cookieEnabled: navigator.cookieEnabled,
            doNotTrack: navigator.doNotTrack,
            onLine: navigator.onLine,
            javaEnabled: navigator.javaEnabled ? navigator.javaEnabled() : false,
            timestamp: Date.now()
        };
    }
    
    /**
     * Obtém informações do navegador
     */
    async getBrowserInfo() {
        const info = {
            name: this.getBrowserName(),
            version: this.getBrowserVersion(),
            engine: this.getBrowserEngine(),
            vendor: navigator.vendor || '',
            buildID: navigator.buildID || '',
            product: navigator.product || '',
            productSub: navigator.productSub || '',
            appName: navigator.appName || '',
            appVersion: navigator.appVersion || '',
            appCodeName: navigator.appCodeName || '',
            oscpu: navigator.oscpu || '',
            plugins: this.getPluginInfo(),
            mimeTypes: this.getMimeTypeInfo(),
            webdriver: navigator.webdriver || false,
            permissions: await this.getPermissionInfo(),
            features: this.getBrowserFeatures()
        };
        
        return info;
    }
    
    /**
     * Identifica nome do navegador
     */
    getBrowserName() {
        const userAgent = navigator.userAgent.toLowerCase();
        
        if (userAgent.includes('firefox')) return 'Firefox';
        if (userAgent.includes('chrome') && !userAgent.includes('edg')) return 'Chrome';
        if (userAgent.includes('safari') && !userAgent.includes('chrome')) return 'Safari';
        if (userAgent.includes('edg')) return 'Edge';
        if (userAgent.includes('opera') || userAgent.includes('opr')) return 'Opera';
        if (userAgent.includes('trident')) return 'Internet Explorer';
        
        return 'Unknown';
    }
    
    /**
     * Obtém versão do navegador
     */
    getBrowserVersion() {
        const userAgent = navigator.userAgent;
        const browserName = this.getBrowserName().toLowerCase();
        
        let version = 'Unknown';
        
        try {
            switch (browserName) {
                case 'chrome':
                    version = userAgent.match(/Chrome\/(\d+\.\d+\.\d+\.\d+)/)?.[1] || 'Unknown';
                    break;
                case 'firefox':
                    version = userAgent.match(/Firefox\/(\d+\.\d+)/)?.[1] || 'Unknown';
                    break;
                case 'safari':
                    version = userAgent.match(/Version\/(\d+\.\d+)/)?.[1] || 'Unknown';
                    break;
                case 'edge':
                    version = userAgent.match(/Edg\/(\d+\.\d+\.\d+\.\d+)/)?.[1] || 'Unknown';
                    break;
                case 'opera':
                    version = userAgent.match(/(?:Opera|OPR)\/(\d+\.\d+)/)?.[1] || 'Unknown';
                    break;
            }
        } catch (error) {
            this.logger.debug('Erro ao extrair versão do navegador', { error: error.message });
        }
        
        return version;
    }
    
    /**
     * Identifica engine do navegador
     */
    getBrowserEngine() {
        const userAgent = navigator.userAgent.toLowerCase();
        
        if (userAgent.includes('webkit')) {
            if (userAgent.includes('blink')) return 'Blink';
            return 'WebKit';
        }
        if (userAgent.includes('gecko')) return 'Gecko';
        if (userAgent.includes('trident')) return 'Trident';
        if (userAgent.includes('presto')) return 'Presto';
        
        return 'Unknown';
    }
    
    /**
     * Obtém informações de plugins
     */
    getPluginInfo() {
        const plugins = [];
        
        try {
            for (let i = 0; i < navigator.plugins.length; i++) {
                const plugin = navigator.plugins[i];
                plugins.push({
                    name: plugin.name,
                    filename: plugin.filename,
                    description: plugin.description,
                    version: plugin.version || 'Unknown'
                });
            }
        } catch (error) {
            this.logger.debug('Erro ao obter informações de plugins', { error: error.message });
        }
        
        return plugins;
    }
    
    /**
     * Obtém informações de tipos MIME
     */
    getMimeTypeInfo() {
        const mimeTypes = [];
        
        try {
            for (let i = 0; i < navigator.mimeTypes.length; i++) {
                const mimeType = navigator.mimeTypes[i];
                mimeTypes.push({
                    type: mimeType.type,
                    description: mimeType.description,
                    suffixes: mimeType.suffixes
                });
            }
        } catch (error) {
            this.logger.debug('Erro ao obter tipos MIME', { error: error.message });
        }
        
        return mimeTypes;
    }
    
    /**
     * Obtém informações de permissões
     */
    async getPermissionInfo() {
        const permissions = {};
        
        if (!navigator.permissions) {
            return permissions;
        }
        
        const permissionsToCheck = [
            'geolocation',
            'notifications',
            'camera',
            'microphone',
            'clipboard-read',
            'clipboard-write'
        ];
        
        for (const permission of permissionsToCheck) {
            try {
                const result = await navigator.permissions.query({ name: permission });
                permissions[permission] = result.state;
            } catch (error) {
                permissions[permission] = 'unknown';
            }
        }
        
        return permissions;
    }
    
    /**
     * Obtém recursos do navegador
     */
    getBrowserFeatures() {
        return {
            // APIs modernas
            serviceWorker: 'serviceWorker' in navigator,
            webGL: this.isWebGLSupported(),
            webGL2: this.isWebGL2Supported(),
            webRTC: this.isWebRTCSupported(),
            webAudio: 'AudioContext' in window || 'webkitAudioContext' in window,
            webSpeech: 'speechSynthesis' in window,
            webVR: 'getVRDisplays' in navigator,
            webXR: 'xr' in navigator,
            
            // Storage
            localStorage: this.isLocalStorageSupported(),
            sessionStorage: this.isSessionStorageSupported(),
            indexedDB: 'indexedDB' in window,
            webSQL: 'openDatabase' in window,
            
            // Comunicação
            webSockets: 'WebSocket' in window,
            eventSource: 'EventSource' in window,
            fetch: 'fetch' in window,
            
            // Multimídia
            canvas: 'HTMLCanvasElement' in window,
            svg: 'SVGElement' in window,
            video: 'HTMLVideoElement' in window,
            audio: 'HTMLAudioElement' in window,
            
            // Sensores e dispositivos
            deviceMotion: 'DeviceMotionEvent' in window,
            deviceOrientation: 'DeviceOrientationEvent' in window,
            touch: 'ontouchstart' in window,
            pointer: 'onpointerdown' in window,
            
            // Segurança
            crypto: 'crypto' in window && 'subtle' in crypto,
            csp: this.hasContentSecurityPolicy(),
            
            // Performance
            performance: 'performance' in window,
            observer: 'IntersectionObserver' in window,
            
            // Outros
            fullscreen: 'requestFullscreen' in document.documentElement,
            clipboard: 'clipboard' in navigator,
            share: 'share' in navigator,
            bluetooth: 'bluetooth' in navigator,
            usb: 'usb' in navigator
        };
    }
    
    /**
     * Obtém informações da tela
     */
    async getScreenInfo() {
        const screen = window.screen;
        
        return {
            width: screen.width,
            height: screen.height,
            availWidth: screen.availWidth,
            availHeight: screen.availHeight,
            colorDepth: screen.colorDepth,
            pixelDepth: screen.pixelDepth,
            orientation: screen.orientation ? {
                angle: screen.orientation.angle,
                type: screen.orientation.type
            } : null,
            devicePixelRatio: window.devicePixelRatio || 1,
            viewport: {
                width: window.innerWidth,
                height: window.innerHeight,
                outerWidth: window.outerWidth,
                outerHeight: window.outerHeight
            },
            zoom: this.getZoomLevel(),
            touchSupport: this.getTouchSupport()
        };
    }
    
    /**
     * Obtém informações de hardware
     */
    async getHardwareInfo() {
        const info = {
            cores: navigator.hardwareConcurrency || 'Unknown',
            memory: navigator.deviceMemory || 'Unknown',
            connection: this.getConnectionInfo(),
            battery: await this.getBatteryInfo(),
            gamepad: this.getGamepadInfo(),
            keyboard: this.getKeyboardInfo()
        };
        
        return info;
    }
    
    /**
     * Obtém informações de conexão
     */
    getConnectionInfo() {
        const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
        
        if (!connection) {
            return null;
        }
        
        return {
            effectiveType: connection.effectiveType,
            type: connection.type,
            downlink: connection.downlink,
            downlinkMax: connection.downlinkMax,
            rtt: connection.rtt,
            saveData: connection.saveData
        };
    }
    
    /**
     * Obtém informações da bateria
     */
    async getBatteryInfo() {
        try {
            if (!navigator.getBattery) {
                return null;
            }
            
            const battery = await navigator.getBattery();
            
            return {
                charging: battery.charging,
                chargingTime: battery.chargingTime,
                dischargingTime: battery.dischargingTime,
                level: battery.level
            };
        } catch (error) {
            this.logger.debug('Erro ao obter informações da bateria', { error: error.message });
            return null;
        }
    }
    
    /**
     * Obtém informações de gamepad
     */
    getGamepadInfo() {
        try {
            const gamepads = navigator.getGamepads ? navigator.getGamepads() : [];
            const connectedGamepads = [];
            
            for (let i = 0; i < gamepads.length; i++) {
                if (gamepads[i]) {
                    connectedGamepads.push({
                        id: gamepads[i].id,
                        index: gamepads[i].index,
                        connected: gamepads[i].connected,
                        mapping: gamepads[i].mapping,
                        axes: gamepads[i].axes.length,
                        buttons: gamepads[i].buttons.length
                    });
                }
            }
            
            return {
                supported: 'getGamepads' in navigator,
                connected: connectedGamepads
            };
        } catch (error) {
            this.logger.debug('Erro ao obter informações de gamepad', { error: error.message });
            return { supported: false, connected: [] };
        }
    }
    
    /**
     * Obtém informações do teclado
     */
    getKeyboardInfo() {
        return {
            layoutMap: 'keyboard' in navigator && 'getLayoutMap' in navigator.keyboard,
            lock: 'keyboard' in navigator && 'lock' in navigator.keyboard
        };
    }
    
    /**
     * Obtém informações de rede
     */
    async getNetworkInfo() {
        const info = {
            connection: this.getConnectionInfo(),
            ip: await this.getIPInfo(),
            dns: await this.getDNSInfo(),
            proxy: this.getProxyInfo(),
            vpn: this.getVPNInfo()
        };
        
        return info;
    }
    
    /**
     * Obtém informações de IP
     */
    async getIPInfo() {
        try {
            // Tenta obter IP via WebRTC
            const webRTCIP = await this.getWebRTCIP();
            
            // Tenta obter IP público via API externa (se configurado)
            let publicIP = null;
            if (this.config.enablePublicIPCheck) {
                publicIP = await this.getPublicIP();
            }
            
            return {
                webRTC: webRTCIP,
                public: publicIP,
                consistency: webRTCIP && publicIP ? webRTCIP === publicIP : null
            };
        } catch (error) {
            this.logger.debug('Erro ao obter informações de IP', { error: error.message });
            return null;
        }
    }
    
    /**
     * Obtém IP via WebRTC
     */
    async getWebRTCIP() {
        return new Promise((resolve) => {
            try {
                const pc = new RTCPeerConnection({ iceServers: [{ urls: 'stun:stun.l.google.com:19302' }] });
                const noop = () => {};
                
                pc.createDataChannel('');
                pc.createOffer().then(pc.setLocalDescription.bind(pc), noop);
                
                pc.onicecandidate = (ice) => {
                    if (!ice || !ice.candidate || !ice.candidate.candidate) {
                        return;
                    }
                    
                    const candidate = ice.candidate.candidate;
                    const ipMatch = candidate.match(/([0-9]{1,3}(\.[0-9]{1,3}){3}|[a-f0-9]{1,4}(:[a-f0-9]{1,4}){7})/);
                    
                    if (ipMatch) {
                        resolve(ipMatch[1]);
                        pc.close();
                    }
                };
                
                setTimeout(() => {
                    resolve(null);
                    pc.close();
                }, 5000);
                
            } catch (error) {
                resolve(null);
            }
        });
    }
    
    /**
     * Obtém IP público via API externa
     */
    async getPublicIP() {
        this.logger.debug('Public IP detection skipped due to CORS restrictions');
        // External IP detection would require CORS-enabled endpoint or server-side proxy
        // This could be implemented via the backend API if needed
        return null;
    }
    
    /**
     * Obtém informações de DNS
     */
    async getDNSInfo() {
        // Implementação básica - pode ser expandida
        return {
            leakProtection: this.checkDNSLeakProtection()
        };
    }
    
    /**
     * Verifica proteção contra vazamento de DNS
     */
    checkDNSLeakProtection() {
        // Implementação simplificada
        return 'unknown';
    }
    
    /**
     * Detecta indicadores de proxy
     */
    getProxyInfo() {
        const indicators = [];
        
        // Verifica headers suspeitos (limitado no browser)
        if (navigator.userAgent.toLowerCase().includes('proxy')) {
            indicators.push('User-Agent contém "proxy"');
        }
        
        return {
            detected: indicators.length > 0,
            indicators
        };
    }
    
    /**
     * Detecta indicadores de VPN
     */
    getVPNInfo() {
        const indicators = [];
        
        // Verifica timezone vs localização esperada
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        if (timezone) {
            // Lógica simplificada - pode ser expandida
            indicators.push(`Timezone: ${timezone}`);
        }
        
        return {
            detected: false, // Detecção limitada no browser
            indicators
        };
    }
    
    /**
     * Obtém informações de localização
     */
    async getLocationInfo() {
        const info = {
            timezone: this.getTimezoneInfo(),
            language: this.getLanguageInfo(),
            geolocation: await this.getGeolocationInfo()
        };
        
        return info;
    }
    
    /**
     * Obtém informações de timezone
     */
    getTimezoneInfo() {
        try {
            const options = Intl.DateTimeFormat().resolvedOptions();
            
            return {
                name: options.timeZone,
                offset: new Date().getTimezoneOffset(),
                locale: options.locale,
                calendar: options.calendar,
                numberingSystem: options.numberingSystem
            };
        } catch (error) {
            this.logger.debug('Erro ao obter informações de timezone', { error: error.message });
            return null;
        }
    }
    
    /**
     * Obtém informações de idioma
     */
    getLanguageInfo() {
        return {
            primary: navigator.language,
            all: navigator.languages || [],
            rtl: this.isRTLLanguage(navigator.language)
        };
    }
    
    /**
     * Verifica se é idioma RTL
     */
    isRTLLanguage(language) {
        const rtlLanguages = ['ar', 'he', 'fa', 'ur', 'yi'];
        const langCode = language.split('-')[0].toLowerCase();
        return rtlLanguages.includes(langCode);
    }
    
    /**
     * Obtém informações de geolocalização
     */
    async getGeolocationInfo() {
        if (!navigator.geolocation || !this.config.enableGeolocation) {
            return null;
        }
        
        return new Promise((resolve) => {
            const options = {
                timeout: 10000,
                maximumAge: 300000, // 5 minutos
                enableHighAccuracy: false
            };
            
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    resolve({
                        latitude: position.coords.latitude,
                        longitude: position.coords.longitude,
                        accuracy: position.coords.accuracy,
                        altitude: position.coords.altitude,
                        altitudeAccuracy: position.coords.altitudeAccuracy,
                        heading: position.coords.heading,
                        speed: position.coords.speed,
                        timestamp: position.timestamp
                    });
                },
                (error) => {
                    this.logger.debug('Erro ao obter geolocalização', { error: error.message });
                    resolve(null);
                },
                options
            );
        });
    }
    
    /**
     * Obtém informações de mídia
     */
    async getMediaInfo() {
        const info = {
            devices: await this.getMediaDevices(),
            codecs: this.getSupportedCodecs(),
            formats: this.getSupportedFormats()
        };
        
        return info;
    }
    
    /**
     * Obtém dispositivos de mídia
     */
    async getMediaDevices() {
        try {
            if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
                return null;
            }
            
            const devices = await navigator.mediaDevices.enumerateDevices();
            const deviceInfo = {
                audioInput: 0,
                audioOutput: 0,
                videoInput: 0,
                total: devices.length
            };
            
            devices.forEach(device => {
                switch (device.kind) {
                    case 'audioinput':
                        deviceInfo.audioInput++;
                        break;
                    case 'audiooutput':
                        deviceInfo.audioOutput++;
                        break;
                    case 'videoinput':
                        deviceInfo.videoInput++;
                        break;
                }
            });
            
            return deviceInfo;
        } catch (error) {
            this.logger.debug('Erro ao obter dispositivos de mídia', { error: error.message });
            return null;
        }
    }
    
    /**
     * Obtém codecs suportados
     */
    getSupportedCodecs() {
        const codecs = {
            audio: [],
            video: []
        };
        
        // Testa codecs de áudio
        const audioCodecs = [
            'audio/mpeg',
            'audio/ogg',
            'audio/wav',
            'audio/aac',
            'audio/webm',
            'audio/flac'
        ];
        
        audioCodecs.forEach(codec => {
            const audio = document.createElement('audio');
            if (audio.canPlayType && audio.canPlayType(codec) !== '') {
                codecs.audio.push(codec);
            }
        });
        
        // Testa codecs de vídeo
        const videoCodecs = [
            'video/mp4',
            'video/webm',
            'video/ogg',
            'video/avi',
            'video/mov'
        ];
        
        videoCodecs.forEach(codec => {
            const video = document.createElement('video');
            if (video.canPlayType && video.canPlayType(codec) !== '') {
                codecs.video.push(codec);
            }
        });
        
        return codecs;
    }
    
    /**
     * Obtém formatos suportados
     */
    getSupportedFormats() {
        return {
            image: this.getSupportedImageFormats(),
            font: this.getSupportedFontFormats()
        };
    }
    
    /**
     * Obtém formatos de imagem suportados
     */
    getSupportedImageFormats() {
        const formats = [];
        const canvas = document.createElement('canvas');
        
        if (canvas.toDataURL) {
            // Testa WebP
            if (canvas.toDataURL('image/webp').indexOf('data:image/webp') === 0) {
                formats.push('webp');
            }
            
            // Testa AVIF
            if (canvas.toDataURL('image/avif').indexOf('data:image/avif') === 0) {
                formats.push('avif');
            }
            
            // Formatos básicos sempre suportados
            formats.push('jpeg', 'png', 'gif');
        }
        
        return formats;
    }
    
    /**
     * Obtém formatos de fonte suportados
     */
    getSupportedFontFormats() {
        const formats = [];
        
        // Testa suporte a diferentes formatos de fonte
        const testFormats = [
            { format: 'woff2', test: 'font-face' },
            { format: 'woff', test: 'font-face' },
            { format: 'truetype', test: 'font-face' },
            { format: 'opentype', test: 'font-face' }
        ];
        
        testFormats.forEach(({ format }) => {
            // Implementação simplificada
            formats.push(format);
        });
        
        return formats;
    }
    
    /**
     * Obtém informações de sensores
     */
    async getSensorInfo() {
        const info = {
            deviceMotion: this.getDeviceMotionInfo(),
            deviceOrientation: this.getDeviceOrientationInfo(),
            ambient: await this.getAmbientSensorInfo()
        };
        
        return info;
    }
    
    /**
     * Obtém informações de movimento do dispositivo
     */
    getDeviceMotionInfo() {
        return {
            supported: 'DeviceMotionEvent' in window,
            permissionRequired: 'DeviceMotionEvent' in window && typeof DeviceMotionEvent.requestPermission === 'function'
        };
    }
    
    /**
     * Obtém informações de orientação do dispositivo
     */
    getDeviceOrientationInfo() {
        return {
            supported: 'DeviceOrientationEvent' in window,
            permissionRequired: 'DeviceOrientationEvent' in window && typeof DeviceOrientationEvent.requestPermission === 'function'
        };
    }
    
    /**
     * Obtém informações de sensores ambientais
     */
    async getAmbientSensorInfo() {
        const sensors = {
            light: 'AmbientLightSensor' in window,
            proximity: 'ProximitySensor' in window,
            temperature: 'TemperatureSensor' in window,
            humidity: 'HumiditySensor' in window
        };
        
        return sensors;
    }
    
    /**
     * Obtém informações de segurança
     */
    async getSecurityInfo() {
        const info = {
            https: location.protocol === 'https:',
            csp: this.hasContentSecurityPolicy(),
            mixedContent: this.hasMixedContent(),
            permissions: await this.getSecurityPermissions(),
            features: this.getSecurityFeatures(),
            privacy: this.getPrivacyInfo()
        };
        
        return info;
    }
    
    /**
     * Verifica se tem Content Security Policy
     */
    hasContentSecurityPolicy() {
        const metaTags = document.getElementsByTagName('meta');
        for (let i = 0; i < metaTags.length; i++) {
            if (metaTags[i].getAttribute('http-equiv') === 'Content-Security-Policy') {
                return true;
            }
        }
        return false;
    }
    
    /**
     * Verifica conteúdo misto
     */
    hasMixedContent() {
        return location.protocol === 'https:' && document.querySelector('script[src^="http:"], link[href^="http:"], img[src^="http:"]') !== null;
    }
    
    /**
     * Obtém permissões de segurança
     */
    async getSecurityPermissions() {
        const permissions = {};
        
        if (navigator.permissions) {
            const securityPermissions = ['camera', 'microphone', 'geolocation', 'notifications'];
            
            for (const permission of securityPermissions) {
                try {
                    const result = await navigator.permissions.query({ name: permission });
                    permissions[permission] = result.state;
                } catch (error) {
                    permissions[permission] = 'unknown';
                }
            }
        }
        
        return permissions;
    }
    
    /**
     * Obtém recursos de segurança
     */
    getSecurityFeatures() {
        return {
            crypto: 'crypto' in window && 'subtle' in crypto,
            secureContext: 'isSecureContext' in window ? window.isSecureContext : false,
            crossOriginIsolated: 'crossOriginIsolated' in window ? window.crossOriginIsolated : false
        };
    }
    
    /**
     * Obtém informações de privacidade
     */
    getPrivacyInfo() {
        return {
            doNotTrack: navigator.doNotTrack === '1',
            globalPrivacyControl: navigator.globalPrivacyControl === true,
            incognito: this.detectIncognitoMode(),
            adBlocker: this.detectAdBlocker()
        };
    }
    
    /**
     * Detecta modo incógnito
     */
    detectIncognitoMode() {
        try {
            // Método para Chrome/Edge
            if ('storage' in navigator && 'estimate' in navigator.storage) {
                return navigator.storage.estimate().then(estimate => {
                    return estimate.quota < 120000000; // Menos que ~120MB indica incógnito
                });
            }
            
            // Método para Firefox
            if ('MozAppearance' in document.documentElement.style) {
                const db = indexedDB.open('test');
                return new Promise(resolve => {
                    db.onerror = () => resolve(true);
                    db.onsuccess = () => resolve(false);
                });
            }
            
            // Método para Safari
            try {
                localStorage.setItem('test', '1');
                localStorage.removeItem('test');
                return false;
            } catch (e) {
                return true;
            }
        } catch (error) {
            return false;
        }
    }
    
    /**
     * Detecta bloqueador de anúncios
     */
    detectAdBlocker() {
        // Implementação simplificada
        const testElement = document.createElement('div');
        testElement.innerHTML = '&nbsp;';
        testElement.className = 'adsbox';
        testElement.style.position = 'absolute';
        testElement.style.left = '-10000px';
        
        document.body.appendChild(testElement);
        
        const isBlocked = testElement.offsetHeight === 0;
        document.body.removeChild(testElement);
        
        return isBlocked;
    }
    
    /**
     * Obtém informações de performance
     */
    async getPerformanceInfo() {
        if (!('performance' in window)) {
            return null;
        }
        
        const info = {
            timing: this.getPerformanceTiming(),
            memory: this.getMemoryInfo(),
            navigation: this.getNavigationInfo(),
            resources: this.getResourceTiming()
        };
        
        return info;
    }
    
    /**
     * Obtém timing de performance
     */
    getPerformanceTiming() {
        if (!performance.timing) {
            return null;
        }
        
        const timing = performance.timing;
        
        return {
            domContentLoaded: timing.domContentLoadedEventEnd - timing.navigationStart,
            loadComplete: timing.loadEventEnd - timing.navigationStart,
            domInteractive: timing.domInteractive - timing.navigationStart,
            firstPaint: this.getFirstPaint()
        };
    }
    
    /**
     * Obtém primeiro paint
     */
    getFirstPaint() {
        if (performance.getEntriesByType) {
            const paintEntries = performance.getEntriesByType('paint');
            const firstPaint = paintEntries.find(entry => entry.name === 'first-paint');
            return firstPaint ? firstPaint.startTime : null;
        }
        return null;
    }
    
    /**
     * Obtém informações de memória
     */
    getMemoryInfo() {
        if (performance.memory) {
            return {
                used: performance.memory.usedJSHeapSize,
                total: performance.memory.totalJSHeapSize,
                limit: performance.memory.jsHeapSizeLimit
            };
        }
        return null;
    }
    
    /**
     * Obtém informações de navegação
     */
    getNavigationInfo() {
        if (performance.navigation) {
            return {
                type: performance.navigation.type,
                redirectCount: performance.navigation.redirectCount
            };
        }
        return null;
    }
    
    /**
     * Obtém timing de recursos
     */
    getResourceTiming() {
        if (!performance.getEntriesByType) {
            return null;
        }
        
        const resources = performance.getEntriesByType('resource');
        
        return {
            count: resources.length,
            totalSize: resources.reduce((total, resource) => total + (resource.transferSize || 0), 0),
            avgDuration: resources.length > 0 ? resources.reduce((total, resource) => total + resource.duration, 0) / resources.length : 0
        };
    }
    
    // Métodos auxiliares
    
    isWebGLSupported() {
        try {
            const canvas = document.createElement('canvas');
            return !!(canvas.getContext('webgl') || canvas.getContext('experimental-webgl'));
        } catch (e) {
            return false;
        }
    }
    
    isWebGL2Supported() {
        try {
            const canvas = document.createElement('canvas');
            return !!canvas.getContext('webgl2');
        } catch (e) {
            return false;
        }
    }
    
    isWebRTCSupported() {
        return !!(window.RTCPeerConnection || window.mozRTCPeerConnection || window.webkitRTCPeerConnection);
    }
    
    isLocalStorageSupported() {
        try {
            const test = 'test';
            localStorage.setItem(test, test);
            localStorage.removeItem(test);
            return true;
        } catch (e) {
            return false;
        }
    }
    
    isSessionStorageSupported() {
        try {
            const test = 'test';
            sessionStorage.setItem(test, test);
            sessionStorage.removeItem(test);
            return true;
        } catch (e) {
            return false;
        }
    }
    
    getZoomLevel() {
        return Math.round((window.outerWidth / window.innerWidth) * 100) / 100;
    }
    
    getTouchSupport() {
        return {
            supported: 'ontouchstart' in window,
            maxTouchPoints: navigator.maxTouchPoints || 0,
            touchEvent: 'TouchEvent' in window,
            pointerEvent: 'PointerEvent' in window
        };
    }
    
    /**
     * Obtém estatísticas do serviço
     */
    getStats() {
        return {
            collectionsPerformed: this.stats.collectionsPerformed,
            cacheHits: this.stats.cacheHits,
            cacheMisses: this.stats.cacheMisses,
            errors: this.stats.errors,
            cacheSize: this.cache.size,
            hitRate: this.stats.cacheHits + this.stats.cacheMisses > 0 ? 
                (this.stats.cacheHits / (this.stats.cacheHits + this.stats.cacheMisses) * 100).toFixed(2) + '%' : '0%'
        };
    }
    
    /**
     * Limpa cache
     */
    clearCache() {
        this.cache.clear();
        this.logger.debug('Cache do DeviceService limpo');
    }
    
    /**
     * Atualiza configuração
     */
    updateConfig(newConfig) {
        this.config = newConfig;
        this.clearCache(); // Limpa cache para refletir novas configurações
        this.logger.debug('Configuração do DeviceService atualizada');
    }
    
    /**
     * Testa suporte a coleta de dispositivo
     */
    static isSupported() {
        return !!(navigator && window && document);
    }
    
    /**
     * Obtém capacidades de coleta
     */
    static getCapabilities() {
        return {
            supported: DeviceService.isSupported(),
            basicInfo: !!navigator.userAgent,
            screenInfo: !!window.screen,
            performanceInfo: 'performance' in window,
            mediaDevices: !!(navigator.mediaDevices && navigator.mediaDevices.enumerateDevices),
            geolocation: !!navigator.geolocation,
            battery: !!navigator.getBattery,
            connection: !!(navigator.connection || navigator.mozConnection || navigator.webkitConnection),
            permissions: !!navigator.permissions,
            sensors: 'DeviceMotionEvent' in window || 'DeviceOrientationEvent' in window
        };
    }
    
    /**
     * Alias para collectDeviceInfo (compatibilidade)
     */
    async getDeviceInfo() {
        return await this.collectDeviceInfo();
    }

    /**
     * Limpa recursos
     */
    destroy() {
        this.clearCache();
        this.logger.debug('DeviceService destruído');
    }
}